import React, { useEffect, FC, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Carousel from '@components/Carousel';
import Main from '@components/Layout/Main';
import { HandledComponentError } from '@components/ErrorHandling';
import { ContentDuck } from '../../../redux/content/content.duck';
import FAQs from '@components/FAQs';
import OffersList from '../../OffersList/Configurable';
import { useLoadingReset } from '@hooks/useLoadingReset';
import { useTranslations } from '@hooks/useTranslations';
import { useMyAccountRedirect } from '@hooks/useMyAccountRedirect';
import { useScrollToPositionOnMount } from '@hooks/useScrollToPositionOnMount';
import { useGTM } from '@hooks/useGTM';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { IS_B2B } from '../../../constants/main';
import { Redux } from '../../../redux';
import CarDetailsDuck from '../../../redux/carDetails/carDetails.duck';
import ConfiguratorDuck from '../../../redux/configurator/configurator.duck';
import OfferListDuck from '../../../redux/carList/offerlist.duck';
import LoaderOverlay from '../../LoaderOverlay';
import { GtmData } from '../../../types/gtm';

const ConfigurableHomepage: FC = memo(() => {
    const dispatch = useDispatch();

    const { t } = useTranslations();
    const { pushHomePageLoad, pushToDataLayer } = useGTM();
    const { handleScrollTo } = useScrollToPositionOnMount();
    const { isStockJourney } = useCarStockJourneyCheck();

    const banners = useSelector((state: Redux) => ContentDuck.getConfigurableBanners(state));
    const carouselAutoPlay = useSelector((state: Redux) => ContentDuck.getDynamicBannersSetting(state));
    const filteredOffers = useSelector(state => OfferListDuck.getFilteredOffers(state));

    const fullBrand = t('title');

    useEffect(() => {
        pushHomePageLoad(false);
        pushToDataLayer({
            eventCategory: 'SolRetail::VehicleList',
            eventAction: 'VehicleModelsView::NonInteractive',
            eventLabel: 'Vehicle models view',
        } as GtmData);
        dispatch(CarDetailsDuck.resetCarDetails());
        dispatch(ConfiguratorDuck.resetConfigurator());
    }, []);

    useEffect(() => handleScrollTo(), [handleScrollTo]);
    useLoadingReset();
    useMyAccountRedirect();

    const { pushProductImpressions } = useGTM();
    useEffect(() => {
        pushProductImpressions('home', filteredOffers, { fireSPAEvent: false });
    }, [filteredOffers]);

    const homePageCanonicalLink = `/configurable/`;

    try {
        return (
            <Main
                title={IS_B2B ? t('seo.homePage.b2b.title') : t('seo.homePage.title')}
                description={
                    IS_B2B
                        ? t('seo.homePage.b2b.description', { siteName: fullBrand })
                        : t('seo.homePage.description', { siteName: fullBrand })
                }
                canonicalLink={homePageCanonicalLink}
            >
                <Carousel banners={banners} autoPlay={carouselAutoPlay} />
                {!isStockJourney ? <OffersList /> : <LoaderOverlay loading />}
                <FAQs />
            </Main>
        );
    } catch (e) {
        return <HandledComponentError error={e} />;
    }
});
ConfigurableHomepage.displayName = 'ConfigurableHomepage';
export default ConfigurableHomepage;
